import { NavLink, useLocation } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Users,
  UsersRound,
  Building2,
  FolderKanban,
  ListTodo,
  CalendarClock,
  Gauge,
  ShieldAlert,
  GitBranch,
  GitFork,
  Trophy,
  Calendar,
  CalendarDays,
  CalendarOff,
  Layers,
  Rocket,
  Mountain,
  Puzzle,
  BookOpen,
  FileBarChart2,
  Code2,
  Ticket as TicketIcon,
  MessageCircle,
  Settings,
  Sparkles,
  MapPin,
} from "lucide-react";
import { motion } from "framer-motion";

/**
 * Sidebar nav is organised into static section labels (not collapsible).
 * Reference-style layout: generous spacing, muted uppercase section headings,
 * flat lists of icon + label rows underneath. Role-aware filtering stays.
 */
const groups = [
  {
    id: "overview",
    label: "Overview",
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
      { to: "/clients", label: "Clients", icon: Building2, testid: "nav-clients", adminOnly: true, branchAdminOk: true },
    ],
  },
  {
    id: "work",
    label: "Work",
    items: [
      { to: "/projects", label: "Projects", icon: FolderKanban, testid: "nav-projects" },
      { to: "/epics", label: "Epics", icon: Mountain, testid: "nav-epics", hideForRoles: ["client"] },
      { to: "/features", label: "Features", icon: Puzzle, testid: "nav-features", hideForRoles: ["client"] },
      { to: "/backlog", label: "Backlog", icon: Layers, testid: "nav-backlog", hideForRoles: ["client"] },
      { to: "/sprints", label: "Sprints", icon: Rocket, testid: "nav-sprints", hideForRoles: ["client"] },
      { to: "/tasks", label: "Tasks", icon: ListTodo, testid: "nav-tasks" },
      { to: "/tickets", label: "Tickets", icon: TicketIcon, testid: "nav-tickets" },
      { to: "/documentation", label: "Documentation", icon: BookOpen, testid: "nav-docs" },
    ],
  },
  {
    id: "people",
    label: "People",
    items: [
      { to: "/employees", label: "Employees", icon: Users, testid: "nav-employees", hideForRoles: ["client"] },
      { to: "/teams", label: "Teams", icon: UsersRound, testid: "nav-teams", hideForRoles: ["client"] },
      { to: "/attendance", label: "Attendance", icon: CalendarClock, testid: "nav-attendance", hideForRoles: ["client"] },
      { to: "/leaves", label: "Leaves", icon: CalendarOff, testid: "nav-leaves", hideForRoles: ["client"] },
      { to: "/branches", label: "Branches", icon: MapPin, testid: "nav-branches", adminOnly: true },
      { to: "/holidays", label: "Holidays", icon: CalendarDays, testid: "nav-holidays", adminOnly: true, branchAdminOk: true },
    ],
  },
  {
    id: "engineering",
    label: "Engineering",
    items: [
      { to: "/repositories", label: "Repositories", icon: GitFork, testid: "nav-repos", hideForRoles: ["client"] },
      { to: "/git", label: "Git Analytics", icon: GitBranch, testid: "nav-git", hideForRoles: ["client"] },
    ],
  },
  {
    id: "insights",
    label: "Insights",
    items: [
      { to: "/workload", label: "Workload", icon: Gauge, testid: "nav-workload", hideForRoles: ["client"] },
      { to: "/risks", label: "Risk Center", icon: ShieldAlert, testid: "nav-risks", hideForRoles: ["client"] },
      { to: "/leaderboards", label: "Leaderboards", icon: Trophy, testid: "nav-leaderboards", hideForRoles: ["client"] },
      { to: "/reports", label: "Reports", icon: FileBarChart2, testid: "nav-reports", hideForRoles: ["client"] },
    ],
  },
  {
    id: "collaboration",
    label: "Collaboration",
    items: [
      { to: "/chat", label: "Chat", icon: MessageCircle, testid: "nav-chat", hideForRoles: ["client"] },
      { to: "/meetings", label: "Meetings", icon: Calendar, testid: "nav-meetings", hideForRoles: ["client"] },
    ],
  },
  {
    id: "system",
    label: "System",
    items: [
      { to: "/developer-apis", label: "Developer APIs", icon: Code2, testid: "nav-apis", adminOnly: true },
      { to: "/ai", label: "AI Manager", icon: Sparkles, testid: "nav-ai", hideForRoles: ["client"] },
      { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
    ],
  },
];

function visibleItems(items, role, isAdmin) {
  return items.filter((it) => {
    if (it.adminOnly && !isAdmin && !(it.branchAdminOk && role === "branch_admin")) return false;
    if (it.hideForRoles && role && it.hideForRoles.includes(role)) return false;
    return true;
  });
}

function NavRow({ item, pathname }) {
  const Icon = item.icon;
  const active = pathname === item.to || (item.to !== "/" && pathname.startsWith(item.to));
  return (
    <NavLink
      to={item.to}
      data-testid={item.testid}
      end={item.to === "/"}
      className={({ isActive }) => {
        const on = isActive || active;
        return `group relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
          on
            ? "text-foreground"
            : "text-muted-foreground hover:text-foreground hover:bg-accent/40"
        }`;
      }}
    >
      {({ isActive }) => (
        <>
          {(isActive || active) && (
            <motion.span
              layoutId="sidebar-active"
              className="absolute inset-0 rounded-lg bg-primary/15 border border-primary/25"
              transition={{ type: "spring", stiffness: 380, damping: 30 }}
            />
          )}
          <Icon className="w-4 h-4 relative z-10 shrink-0" strokeWidth={1.75} />
          <span className="relative z-10 font-medium">{item.label}</span>
        </>
      )}
    </NavLink>
  );
}

export default function Sidebar() {
  const location = useLocation();
  const { user } = useAuth();
  const role = user?.role;
  const isAdmin = role === "admin";

  return (
    <aside
      data-testid="app-sidebar"
      className="hidden lg:flex flex-col w-64 shrink-0 h-screen sticky top-0 glass-strong border-r"
    >
      <div className="px-5 pt-5 pb-4">
        <BrandLogo className="w-full max-w-[180px] h-auto" />
      </div>

      <nav className="flex-1 overflow-y-auto scroll-thin px-3 pb-6">
        {groups.map((group, gi) => {
          const items = visibleItems(group.items, role, isAdmin);
          if (!items.length) return null;
          return (
            <div
              key={group.id}
              data-testid={`nav-group-${group.id}`}
              className={gi === 0 ? "pt-2" : "pt-6"}
            >
              <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/70">
                {group.label}
              </div>
              <div className="space-y-0.5">
                {items.map((it) => <NavRow key={it.to} item={it} pathname={location.pathname} />)}
              </div>
            </div>
          );
        })}
      </nav>

      <div className="px-4 pb-5 pt-3 border-t border-border/60">
        <div className="text-xs text-muted-foreground">v1.0 · Enterprise</div>
      </div>
    </aside>
  );
}
